import type { CSSProperties } from "react";

import { color, focusRing, font, radius, spaces } from "emoreg/const";
import styled, { css } from "styled-components";

const baseStyles: CSSProperties & {
  "&:focus-within"?: CSSProperties;
} = {
  display: "inline-flex",
  position: "relative",
  width: "100%",
  backgroundColor: "white",
  borderWidth: 1,
  borderStyle: "solid",
  borderRadius: radius.base,
  fontSize: "14px",
  fontFamily: "Source Sans Pro, -apple-system, BlinkMacSystemFont, sans-serif",
  fontWeight: font.weight.normal,
  alignItems: "center",

  "&:focus-within": {
    borderColor: color.blue.base,
    outline: `2px solid ${color.blue.base}`,
    outlineOffset: "-1px",
    boxShadow: focusRing,
  },
};

const Container = styled.div<{
  disabled?: boolean;
  hasError?: boolean;
  size?: "medium" | "large";
}>`
  ${baseStyles}

  ${({ size = "medium" }) =>
    size === "medium"
      ? css({
          height: spaces[32],
          paddingLeft: spaces[12],
          paddingRight: spaces[12],
        })
      : css({
          height: spaces[48],
          paddingLeft: spaces[16],
          paddingRight: spaces[16],
        })}

  /* State variations */
  ${({ disabled, hasError }) => {
    if (disabled) {
      return css({
        backgroundColor: color.grey.light,
        borderColor: color.grey.base,
        color: color.grey.dark,
        cursor: "not-allowed",
      });
    }
    if (hasError) {
      return css({
        borderColor: color.red.base,
        "&:focus-within": {
          borderColor: color.red.base,
          outline: `2px solid ${color.red.base}`,
          outlineOffset: "-1px",
          boxShadow: `0 0 0 2px ${color.red.base}33`,
        },
      });
    }
    return css({
      borderColor: color.grey.base,
      color: color.grey.darker,
      "&:hover": {
        borderColor: color.grey.dark,
      },
    });
  }}
`;

Container.displayName = "InputContainer";
Container.baseName = "InputContainer";

const inputBaseStyles: CSSProperties & {
  "&::placeholder"?: CSSProperties;
  "&:disabled"?: CSSProperties & {
    "&::placeholder"?: CSSProperties;
  };
} = {
  width: "100%",
  border: "none",
  outline: "none",
  background: "transparent",
  fontFamily: "inherit",
  fontSize: "inherit",
  fontWeight: "inherit",
  color: "inherit",
  alignSelf: "stretch",
  flex: 1,

  "&::placeholder": {
    color: color.grey.dark,
    opacity: 1,
  },

  "&:disabled": {
    cursor: "not-allowed",
    color: color.grey.dark,

    "&::placeholder": {
      color: color.grey.base,
    },
  },
};

const InputElement = styled.input<{ disabled?: boolean }>`
  ${inputBaseStyles}
`;

InputElement.displayName = "InputElement";
InputElement.baseName = "Input";

export const InputStyle = Object.assign(Container, { Input: InputElement });
